import { Link, NavLink, useLocation } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { X, ChevronDown } from 'lucide-react'
import { NAV, isNavVisible, type NavItem } from './nav'
import { useAuthStore } from '@/stores/authStore'
import { useSidebarStore } from '@/stores/sidebarStore'
import { cn } from '@/lib/cn'

/**
 * Sidebar — comportamento responsivo:
 *  - lg (≥1024px): fixa à esquerda, sempre visível, ocupa 256px
 *  - <lg: drawer (slide-over) escondido por padrão, abre via hamburger na topbar
 *
 * Fecha automaticamente ao navegar (NavLink click) em mobile.
 */
export function Sidebar() {
  const user = useAuthStore((s) => s.user)
  const userType = user?.type ?? ''
  // ADMINISTRADOR tem acesso total por TIPO (não depende de hierarquia). Demais
  // tipos (colaborador) enxergam itens conforme as authorities da hierarquia.
  const auths: string[] = userType === 'ADMINISTRADOR'
    ? ['*']
    : ((user as { hierarchy?: { authorities?: string[] } })?.hierarchy?.authorities ?? [])
  // Seções com `types` só aparecem para esses tipos (ex.: EMPRESA, parceiros);
  // as demais (painel admin) só para ADMINISTRADOR/COLABORADOR.
  const sections = NAV.filter((s) => (s.types ? s.types.includes(userType) : ['ADMINISTRADOR', 'COLABORADOR'].includes(userType)))
  const open  = useSidebarStore((s) => s.open)
  const close = useSidebarStore((s) => s.close)
  const location = useLocation()

  // Fecha o drawer ao navegar entre rotas (em mobile)
  useEffect(() => { close() }, [location.pathname, close])

  // Item ativo = o `to` mais específico (prefixo mais longo) que casa com a URL.
  // Evita que um item pai (ex.: /commercial/proposals) fique ativo quando a rota
  // é de um item irmão mais específico (ex.: /commercial/proposals/kanban).
  const activeTo = (() => {
    const tos: string[] = []
    for (const s of sections) for (const it of s.items) {
      if (it.children?.length) it.children.forEach((c) => tos.push(c.to))
      else tos.push(it.to)
    }
    const p = location.pathname
    return tos
      .filter((to) => p === to || p.startsWith(to.endsWith('/') ? to : to + '/'))
      .sort((a, b) => b.length - a.length)[0] ?? null
  })()

  // Em mobile, trava o scroll do body quando o drawer está aberto
  useEffect(() => {
    if (open && window.innerWidth < 1024) {
      const prev = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      return () => { document.body.style.overflow = prev }
    }
  }, [open])

  return (
    <>
      {/* Backdrop em mobile — só aparece quando drawer aberto */}
      <button
        type="button"
        onClick={close}
        aria-hidden={!open}
        className={cn(
          'lg:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity',
          open ? 'opacity-100' : 'opacity-0 pointer-events-none',
        )}
      />

      <aside
        className={cn(
          'flex flex-col overflow-y-auto',
          // Desktop: sticky, sempre visível
          'lg:sticky lg:top-0 lg:translate-x-0 lg:h-screen lg:w-64 lg:shrink-0',
          // Mobile: fixed, drawer (slide-over)
          'fixed inset-y-0 left-0 z-50 h-dvh w-72 max-w-[85vw] transition-transform duration-200 ease-out',
          open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        )}
        style={{
          background: 'var(--color-sidebar)',
          color: 'var(--color-sidebar-text)',
        }}
      >
        <div className="relative px-5 py-6 flex items-center justify-center">
          <Link to="/" className="block">
            {/* Mesma logo do login, centralizada. O sidemenu tem fundo escuro
                (var(--color-sidebar)), então usa-se brightness-0 invert para exibi-la
                em branco. Se quiser a logo branca real com o "+" laranja, salve em
                public/logo-white-full.png e troque o src (removendo brightness-0 invert). */}
            <img src="/logo-light-full.png" alt="Ampla+ Benefícios" className="h-12 w-auto object-contain brightness-0 invert" />
          </Link>
          {/* Botão fechar em mobile — sobreposto à direita para não desalinhar a logo */}
          <button
            type="button"
            onClick={close}
            className="cursor-pointer lg:hidden absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/5"
            aria-label="Fechar menu"
          >
            <X size={18} />
          </button>
        </div>

        <nav className="flex-1 px-3 pb-6 space-y-5">
          {sections.map((section) => {
            const visibleItems = section.items.filter((it) => isNavVisible(it, auths) && (!it.types || it.types.includes(userType)))
            if (visibleItems.length === 0) return null
            return (
              <div key={section.title}>
                <div
                  className="px-3 mb-1 text-[10px] uppercase tracking-[0.12em] font-semibold"
                  style={{ color: 'var(--color-sidebar-title)' }}
                >
                  {section.title}
                </div>
                <ul className="space-y-0.5">
                  {visibleItems.map((it) => (
                    <NavItemRow key={it.to} item={it} activeTo={activeTo} />
                  ))}
                </ul>
              </div>
            )
          })}
        </nav>

        <div
          className="px-4 py-3 border-t border-white/5 text-[10px] flex items-center justify-between"
          style={{ color: 'var(--color-sidebar-title)' }}
        >
          <span>Versão 3.0</span>
          <span className="px-1.5 py-0.5 rounded bg-white/5">{new Date().getFullYear()}</span>
        </div>
      </aside>
    </>
  )
}

/**
 * Linha do menu — suporta item simples ou pai com filhos colapsáveis.
 *
 * Pai com `children`:
 *  - renderiza como botão expansível (chevron rotaciona)
 *  - auto-expande se alguma rota filha está ativa
 *  - exibe a lista de filhos como `NavLink`s indentados
 */
function NavItemRow({ item, activeTo }: { item: NavItem; activeTo: string | null }) {
  const hasChildren = !!item.children && item.children.length > 0
  const isChildActive = hasChildren && item.children!.some(c => c.to === activeTo)
  const [expanded, setExpanded] = useState(isChildActive)

  // Mantém expandido quando uma rota filha vira ativa via navegação externa.
  useEffect(() => {
    if (isChildActive) setExpanded(true)
  }, [isChildActive])

  const Icon = item.icon

  if (!hasChildren) {
    return (
      <li>
        <NavLink
          to={item.to}
          className={cn(
            'flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition',
            item.to === activeTo ? 'bg-white/10 text-white' : 'hover:bg-white/5 hover:text-white',
          )}
        >
          <Icon size={16} strokeWidth={1.75} />
          <span>{item.label}</span>
        </NavLink>
      </li>
    )
  }

  return (
    <li>
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        aria-expanded={expanded}
        className={cn(
          'cursor-pointer w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition',
          isChildActive ? 'bg-white/10 text-white' : 'hover:bg-white/5 hover:text-white',
        )}
      >
        <Icon size={16} strokeWidth={1.75} />
        <span className="flex-1 text-left">{item.label}</span>
        <ChevronDown
          size={14}
          className={cn('transition-transform', expanded ? 'rotate-180' : 'rotate-0')}
        />
      </button>
      {expanded && (
        <ul className="mt-0.5 ml-3 pl-3 border-l border-white/10 space-y-0.5">
          {item.children!.map((child) => (
            <li key={child.to}>
              <NavLink
                to={child.to}
                className={cn(
                  'block px-3 py-2 rounded-lg text-[13px] transition',
                  child.to === activeTo ? 'bg-white/10 text-white' : 'text-white/70 hover:bg-white/5 hover:text-white',
                )}
              >
                {child.label}
              </NavLink>
            </li>
          ))}
        </ul>
      )}
    </li>
  )
}
